/**
 * `ColumnHeaderMenu` — the per-column three-dot header menu (MUI `GridColumnMenu`
 * parity): Sort by ASC / Sort by DESC / Unsort and Pin to left / Pin to right / Unpin.
 * Thin by design — it reads and writes the SAME engine state the preset threads
 * (`toggleSorting`/`clearSorting`/`pin`), so it shares one source of truth with the
 * `column.pin()` / `column.toggleSorting()` escape hatches. Mirrors `VisibilityMenu`,
 * but scoped to ONE column and triggered by an icon button rather than a toolbar button.
 *
 * The sort group renders only when `column.getCanSort()`, the pin group only when
 * `column.getCanPin()`; a column that can do neither renders nothing (the caller should
 * not mount it). The active sort direction / pin side is marked `selected`.
 */
import type { Column, RowData } from '@tanstack/react-table'

import { ArrowDownIcon } from '@gto/icons-react/solid/arrow-down'
import { ArrowUpIcon } from '@gto/icons-react/solid/arrow-up'
import { ChevronLeftIcon } from '@gto/icons-react/solid/chevron-left'
import { ChevronRightIcon } from '@gto/icons-react/solid/chevron-right'
import { EllipsisVerticalIcon } from '@gto/icons-react/solid/ellipsis-vertical'
import { MapPinIcon } from '@gto/icons-react/solid/map-pin'
import { XMarkIcon } from '@gto/icons-react/solid/x-mark'
import { cn } from '@gto/ui/cn'
import { IconButton } from '@gto/ui/icon-button'
import { Menu } from '@gto/ui/menu'

import { DEFAULT_COLUMN_HEADER_MENU_LABELS } from './column-header-menu.constants'

export type ColumnHeaderMenuLabels = {
  /** Trigger accessible name. */
  menu: string
  /**
   * Accessible name of the header's SORT BUTTON (the arrow in the affordance cluster) —
   * not a menu item, but it travels with these labels so a localized consumer supplies
   * every header-chrome string through one prop. The English default deliberately matches
   * DataGridPro's `Sort`, which the e2e helpers select on for both engines.
   */
  sortLabel: string
  sortAsc: string
  sortDesc: string
  unsort: string
  pinLeft: string
  pinRight: string
  unpin: string
  moveLeft: string
  moveRight: string
}

/**
 * Keyboard-accessible reorder controls, surfaced as "Move left/right" menu items when column
 * reordering is enabled. This is the keyboard path for reorder (R5/R8): the drag gesture is
 * pointer-only because the grid's single-tab-stop nav model is incompatible with `@dnd-kit`'s
 * per-item drag focus, so the menu (reachable by Tab → Enter) provides the keyboard equivalent.
 */
export type ColumnHeaderMenuMove = {
  onMoveLeft: () => void
  onMoveRight: () => void
  canMoveLeft: boolean
  canMoveRight: boolean
}

export type ColumnHeaderMenuProps<TData extends RowData> = {
  column: Column<TData, unknown>
  labels?: Partial<ColumnHeaderMenuLabels>
  /** When present, renders keyboard-accessible "Move left/right" reorder items. */
  move?: ColumnHeaderMenuMove
}

const ITEM_ICON_CLASS = 'size-4 shrink-0'

/**
 * Plain generic function, not `FC`: `Column` is invariant in `TData`, so a `<TData,>`
 * component can't be assigned to a non-generic `FC` (keeps call-site row-type inference) —
 * same reason as `VisibilityMenu`.
 */
export const ColumnHeaderMenu = <TData extends RowData>({
  column,
  labels,
  move,
}: ColumnHeaderMenuProps<TData>) => {
  const resolved = { ...DEFAULT_COLUMN_HEADER_MENU_LABELS, ...labels }
  const canSort = column.getCanSort()
  const canPin = column.getCanPin()
  const sorted = column.getIsSorted()
  const pinned = column.getIsPinned()

  // Nothing to offer → render nothing (the preset also gates on this, so this is defensive).
  if (!canSort && !canPin && move === undefined) {
    return null
  }

  return (
    <Menu.Root>
      <Menu.Trigger
        render={
          <IconButton
            aria-label={resolved.menu}
            data-column-menu-trigger={column.id}
            shape="square"
            size="small"
            // Keep the trigger's click off the header cell's click-to-sort handler and the
            // reorder drag: stop it here so opening the menu never sorts or starts a drag.
            onClick={(event) => event.stopPropagation()}
            onPointerDown={(event) => event.stopPropagation()}
          >
            <EllipsisVerticalIcon />
          </IconButton>
        }
      />
      <Menu.Portal>
        <Menu.Positioner align="end">
          {/*
            Stop menu clicks from bubbling to the header cell's click-to-sort `onClick`. The
            popup is PORTALED to the body, but React bubbles synthetic events through the
            component TREE (the menu is a child of the header cell), so without this a menu-item
            click (e.g. "Move right") also toggles the column's sort. jsdom's event delegation
            doesn't reproduce this — it only surfaces in a real browser (caught in browser-qa).
          */}
          <Menu.Popup
            className="min-w-[180px]"
            onClick={(event) => event.stopPropagation()}
          >
            {canSort && (
              <Menu.Group>
                <Menu.Item
                  data-column-id={column.id}
                  selected={sorted === 'asc'}
                  onClick={() => column.toggleSorting(false)}
                >
                  <ArrowUpIcon className={ITEM_ICON_CLASS} />
                  {resolved.sortAsc}
                </Menu.Item>
                <Menu.Item
                  data-column-id={column.id}
                  selected={sorted === 'desc'}
                  onClick={() => column.toggleSorting(true)}
                >
                  <ArrowDownIcon className={ITEM_ICON_CLASS} />
                  {resolved.sortDesc}
                </Menu.Item>
                {sorted !== false && (
                  <Menu.Item
                    data-column-id={column.id}
                    onClick={() => column.clearSorting()}
                  >
                    <XMarkIcon className={ITEM_ICON_CLASS} />
                    {resolved.unsort}
                  </Menu.Item>
                )}
              </Menu.Group>
            )}
            {canSort && canPin && <Menu.Separator />}
            {canPin && (
              <Menu.Group>
                <Menu.Item
                  data-column-id={column.id}
                  selected={pinned === 'left'}
                  onClick={() => column.pin('left')}
                >
                  <MapPinIcon className={cn(ITEM_ICON_CLASS, '-scale-x-100')} />
                  {resolved.pinLeft}
                </Menu.Item>
                <Menu.Item
                  data-column-id={column.id}
                  selected={pinned === 'right'}
                  onClick={() => column.pin('right')}
                >
                  <MapPinIcon className={ITEM_ICON_CLASS} />
                  {resolved.pinRight}
                </Menu.Item>
                {pinned !== false && (
                  <Menu.Item
                    data-column-id={column.id}
                    onClick={() => column.pin(false)}
                  >
                    <XMarkIcon className={ITEM_ICON_CLASS} />
                    {resolved.unpin}
                  </Menu.Item>
                )}
              </Menu.Group>
            )}
            {move !== undefined && (canSort || canPin) && <Menu.Separator />}
            {move !== undefined && (
              // Keyboard-accessible reorder (R5/R8): the menu is reachable by Tab → Enter, so
              // these give a keyboard equivalent to the pointer-only drag. Disabled at the ends.
              <Menu.Group>
                <Menu.Item
                  data-column-id={column.id}
                  disabled={!move.canMoveLeft}
                  onClick={move.onMoveLeft}
                >
                  <ChevronLeftIcon className={ITEM_ICON_CLASS} />
                  {resolved.moveLeft}
                </Menu.Item>
                <Menu.Item
                  data-column-id={column.id}
                  disabled={!move.canMoveRight}
                  onClick={move.onMoveRight}
                >
                  <ChevronRightIcon className={ITEM_ICON_CLASS} />
                  {resolved.moveRight}
                </Menu.Item>
              </Menu.Group>
            )}
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  )
}
